/**
 * TrustBar — bandeau de réassurance compact à placer près des CTA/formulaires.
 *
 * Réduit la friction au moment de la conversion en rappelant les preuves clés
 * (mandataire CEE, réseau RGE, sans engagement, gratuité). Réutilisable partout :
 * sous un formulaire lead, en bas d'un funnel, sous un CTA hero.
 *
 * `variant="inline"` : ligne discrète (puces séparées).
 * `variant="cards"`  : mini-cartes avec icône (plus visible, fin de funnel).
 */

import { ShieldCheck, BadgeCheck, Handshake, Clock, CreditCard } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface TrustItem {
  icon: LucideIcon;
  label: string;
}

const DEFAULT_ITEMS: TrustItem[] = [
  { icon: ShieldCheck, label: "Mandataire CEE (certificats d'économie d'énergie) déclaré" },
  { icon: BadgeCheck, label: "Réseau d'artisans RGE (reconnus garant de l'environnement) contrôlé" },
  { icon: Handshake, label: 'Sans engagement' },
  { icon: CreditCard, label: 'Sans carte bancaire' },
  { icon: Clock, label: 'Réponse sous 24 h' },
];

interface TrustBarProps {
  items?: TrustItem[];
  variant?: 'inline' | 'cards';
  className?: string;
  /** Limite le nombre d'items affichés (utile sur mobile). */
  max?: number;
}

export function TrustBar({ items = DEFAULT_ITEMS, variant = 'inline', className, max }: TrustBarProps) {
  const list = typeof max === 'number' ? items.slice(0, max) : items;

  if (variant === 'cards') {
    return (
      <ul className={cn('grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5', className)}>
        {list.map((item) => {
          const Icon = item.icon;
          return (
            <li
              key={item.label}
              className="flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-2.5"
            >
              <Icon className="h-4 w-4 shrink-0 text-brand-600" strokeWidth={2} aria-hidden />
              <span className="text-[12px] font-medium leading-tight text-stone-700">{item.label}</span>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <ul
      className={cn(
        'flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11.5px] text-stone-500',
        className,
      )}
    >
      {list.map((item) => {
        const Icon = item.icon;
        return (
          <li key={item.label} className="inline-flex items-center gap-1.5">
            <Icon className="h-3.5 w-3.5 shrink-0 text-brand-500" strokeWidth={2} aria-hidden />
            <span className="font-medium">{item.label}</span>
          </li>
        );
      })}
    </ul>
  );
}
